import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  Shield, Search, Download, RefreshCw, Loader2,
  Eye, FileText, User, Clock, Filter, X,
  BarChart3, Calendar, AlertTriangle, Trash2
} from 'lucide-react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditoriaRGPD() {
  const { toast } = useToast();
  const [tabActiva, setTabActiva] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    usuario_id: '',
    accion: '',
    recurso: ''
  });

  const COLORES = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    cargarLogs();
    cargarEstadisticas();
  }, []);

  const cargarLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.usuario_id) params.append('usuario_id', filtros.usuario_id);
      if (filtros.accion && filtros.accion !== 'all') params.append('accion', filtros.accion);
      if (filtros.recurso && filtros.recurso !== 'all') params.append('recurso', filtros.recurso);

      const response = await axios.get(`${API}/auditoria/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error cargando logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const response = await axios.get(`${API}/auditoria/estadisticas?dias=30`);
      setEstadisticas(response.data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  };

  const exportarLogs = async (formato) => {
    try {
      const response = await axios.get(`${API}/auditoria/exportar?formato=${formato}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auditoria_rgpd.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({ title: 'Exportación completada', description: `Archivo ${formato.toUpperCase()} descargado` });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };

  const getAccionBadge = (accion) => {
    const estilos = {
      login: 'bg-blue-100 text-blue-700',
      logout: 'bg-gray-100 text-gray-700',
      ver: 'bg-green-100 text-green-700',
      crear: 'bg-emerald-100 text-emerald-700',
      modificar: 'bg-yellow-100 text-yellow-700',
      eliminar: 'bg-red-100 text-red-700',
      exportar: 'bg-purple-100 text-purple-700',
      acceso_denegado: 'bg-red-200 text-red-800'
    };
    return estilos[accion] || 'bg-gray-100 text-gray-700';
  };

  const limpiarFiltros = () => {
    setFiltros({
      fecha_desde: '',
      fecha_hasta: '',
      usuario_id: '',
      accion: '',
      recurso: ''
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Auditoría RGPD
          </h1>
          <p className="text-gray-600">Registro de accesos y operaciones para cumplimiento normativo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportarLogs('csv')}>
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => exportarLogs('json')}>
            <Download className="h-4 w-4 mr-2" /> JSON
          </Button>
        </div>
      </div>

      <Tabs value={tabActiva} onValueChange={setTabActiva}>
        <TabsList className="mb-4">
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Logs de Acceso
          </TabsTrigger>
          <TabsTrigger value="estadisticas" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Estadísticas
          </TabsTrigger>
        </TabsList>

        {/* Logs */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Registro de Accesos</CardTitle>
              <CardDescription>Últimos {logs.length} registros</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtros */}
              <div className="grid md:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Acción</Label>
                  <Select value={filtros.accion} onValueChange={(v) => setFiltros({ ...filtros, accion: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="ver">Ver</SelectItem>
                      <SelectItem value="crear">Crear</SelectItem>
                      <SelectItem value="modificar">Modificar</SelectItem>
                      <SelectItem value="eliminar">Eliminar</SelectItem>
                      <SelectItem value="exportar">Exportar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Recurso</Label>
                  <Select value={filtros.recurso} onValueChange={(v) => setFiltros({ ...filtros, recurso: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="expediente">Expediente</SelectItem>
                      <SelectItem value="contacto">Contacto</SelectItem>
                      <SelectItem value="factura">Factura</SelectItem>
                      <SelectItem value="documento">Documento</SelectItem>
                      <SelectItem value="usuario">Usuario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={cargarLogs} className="flex-1">
                    <Search className="h-4 w-4 mr-1" /> Filtrar
                  </Button>
                  <Button variant="outline" onClick={limpiarFiltros}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Tabla de logs */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Fecha</th>
                        <th className="text-left p-3">Usuario</th>
                        <th className="text-left p-3">Acción</th>
                        <th className="text-left p-3">Recurso</th>
                        <th className="text-left p-3">Detalles</th>
                        <th className="text-left p-3">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-xs text-gray-600">
                            {new Date(log.fecha).toLocaleString('es-ES')}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm">{log.usuario_email}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className={getAccionBadge(log.accion)}>
                              {log.accion}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <span className="text-sm">{log.recurso}</span>
                            {log.recurso_id && (
                              <span className="text-xs text-gray-500 ml-1">#{log.recurso_id}</span>
                            )}
                          </td>
                          <td className="p-3 text-xs text-gray-600 max-w-xs truncate">
                            {log.detalles || '-'}
                          </td>
                          <td className="p-3 text-xs text-gray-500">{log.ip_origen || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {logs.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No hay registros que mostrar</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Estadísticas */}
        <TabsContent value="estadisticas">
          {estadisticas ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Por Acción */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Accesos por Tipo de Acción</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={estadisticas.por_accion}
                          dataKey="total"
                          nameKey="accion"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ accion, total }) => `${accion}: ${total}`}
                        >
                          {estadisticas.por_accion?.map((entry, index) => (
                            <Cell key={index} fill={COLORES[index % COLORES.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Por Usuario */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Usuarios con más Accesos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadisticas.por_usuario} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="email" type="category" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="total" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Por Recurso */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Accesos por Recurso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadisticas.por_recurso}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="recurso" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="total" fill="#10B981" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Evolución diaria */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Evolución de Accesos (Últimos 30 días)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={estadisticas.por_dia}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="total" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
